import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { weightSamplesService } from '../services/apiServices';

export const useWeightTrend = (birdId: number, enabled = true) =>
  useQuery({
    queryKey: ['birds', birdId, 'weight-samples', 'trend'],
    queryFn: () => weightSamplesService.getTrend(birdId),
    enabled,
    staleTime: 2 * 60 * 1000,
  });

export const useWeekStats = (birdId: number, week: number, enabled = true) =>
  useQuery({
    queryKey: ['birds', birdId, 'weight-samples', week],
    queryFn: () => weightSamplesService.getWeekStats(birdId, week),
    enabled: enabled && week >= 1 && week <= 19,
  });

export const useRecordSamples = (birdId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      block_id: number;
      week: number;
      group: 'main' | 'isolated';
      weights: number[];
    }) => weightSamplesService.recordSamples(birdId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['birds', birdId, 'weight-samples'] });
    },
  });
};

export const useDeleteWeek = (birdId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (week: number) => weightSamplesService.deleteWeek(birdId, week),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['birds', birdId, 'weight-samples'] });
    },
  });
};

export const useExtractImage = (birdId: number) =>
  useMutation({
    mutationFn: (formData: FormData) => weightSamplesService.extractImage(birdId, formData),
  });

export const useUpdateSample = (birdId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ sampleId, weightG }: { sampleId: number; weightG: number }) =>
      weightSamplesService.updateSample(birdId, sampleId, weightG),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['birds', birdId, 'weight-samples'] });
    },
  });
};

export const useRegroupBlock = (birdId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { block_id: number; week: number; group: 'main' | 'isolated' }) =>
      weightSamplesService.regroupBlock(birdId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['birds', birdId, 'weight-samples'] });
    },
  });
};
